import { useMedicationLookup } from "@tellescope/tanstack-query";
import {
  Button,
  Combobox,
  Input,
  NumberInput,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@tellescope/ui";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { cn, levenshteinDistance } from "@tellescope/utils";
import { MedicationResponse } from "@tellescope/types-models";

export interface AddMedicationFormProps {
  medication?: MedicationResponse;
  onChange: (medication: MedicationResponse) => void;
  onRemove?: () => void;
  className?: string;
}

export const AddMedicationForm = ({
  medication: providedMedication,
  onChange,
  onRemove,
  className,
}: AddMedicationFormProps) => {
  const { t } = useTranslation("forms");
  const medication: MedicationResponse = {
    displayTerm: providedMedication?.displayTerm ?? "",
    drugName: providedMedication?.drugName ?? "",
    otherDrug: providedMedication?.otherDrug,
    dosage: providedMedication?.dosage ?? undefined,
    reasonForTaking: providedMedication?.reasonForTaking,
  };

  const { allDisplayNames, specificDrugOptions } = useMedicationLookup({
    selectedDisplayName: medication.displayTerm,
    selectedSpecificDrugName: medication.drugName,
  });

  const selectedSpecificDrug = useMemo(() => {
    return specificDrugOptions.find((drug) => drug.name === medication.drugName);
  }, [medication.drugName, specificDrugOptions]);

  return (
    <div className={cn("flex flex-col gap-2", className)}>
      <Combobox
        modal
        value={{ value: medication?.displayTerm ?? "", label: medication?.displayTerm ?? "" }}
        onChange={(option) => onChange({ ...medication, displayTerm: option?.value ?? "" })}
        options={allDisplayNames.map((displayName) => ({
          value: displayName,
          label: displayName,
        }))}
        alwaysFilter
        customFilter={(searchTerm, options) => {
          if (searchTerm) {
            return [...options]
              .filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase()))
              .sort((a, b) => a.label.length - b.label.length)
              .slice(0, 50);
          }
          return options.slice(0, 50);
        }}
        placeholder={t("Choose a medication...")}
        searchPlaceholder={t("Search medications...")}
        isLoading={allDisplayNames.length === 0}
        loadingText={t("Loading medications...")}
        emptyText={t("No medications found")}
        clearable
      />
      {!!medication.displayTerm && !medication.otherDrug && !!specificDrugOptions.length && (
        <Combobox
          modal
          value={{
            value: medication.drugName,
            label: selectedSpecificDrug?.synonym ?? medication.drugName,
          }}
          onChange={(option) => onChange({ ...medication, drugName: option?.value ?? "" })}
          options={specificDrugOptions.map((drug) => ({
            value: drug.name,
            label: drug.synonym,
          }))}
          alwaysFilter
          customFilter={(searchTerm, options) => {
            const getSearchableString = (option: (typeof options)[number]): string => {
              return `${option.label}|${option.value}|${option.description ?? ""}`.toLowerCase();
            };

            if (searchTerm) {
              return [...options]
                .filter((option) => getSearchableString(option).includes(searchTerm.toLowerCase()))
                .sort(
                  (a, b) =>
                    levenshteinDistance(getSearchableString(b), searchTerm.toLowerCase()) -
                    levenshteinDistance(getSearchableString(a), searchTerm.toLowerCase())
                );
            }
            return [...options].slice(0, 50);
          }}
          placeholder={t("Drug Select")}
          searchPlaceholder={t("Search specific drugs...")}
          emptyText={t("No specific drugs found")}
          clearable
        />
      )}
      {!!medication.displayTerm && (
        <>
          <Input
            value={medication.otherDrug}
            onChange={(e) => onChange({ ...medication, otherDrug: e.target.value })}
            placeholder={t("Other Drug")}
          />
          <div className="flex gap-2">
            <NumberInput
              value={medication.otherDrug ? parseInt(medication.otherDrug) : undefined}
              onChange={(quantity) =>
                onChange({
                  ...medication,
                  dosage: {
                    ...medication.dosage,
                    quantity: String(quantity),
                    value: medication.dosage?.value ?? "",
                    unit: medication.dosage?.unit ?? "",
                  },
                })
              }
              placeholder={t("Units (e.g. capsule, table, puff) per dose?")}
              className="flex-1 w-full"
            />
            <Select
              value={medication.dosage?.frequency}
              onValueChange={(frequency) =>
                onChange({
                  ...medication,
                  dosage: {
                    ...medication.dosage,
                    frequency,
                    value: medication.dosage?.value ?? "",
                    unit: medication.dosage?.unit ?? "",
                  },
                })
              }
            >
              <SelectTrigger className="flex-1 w-full">
                {medication.dosage?.frequency ? <SelectValue /> : t("How many times per day?")}
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1">{t("1")}</SelectItem>
                <SelectItem value="2">{t("2")}</SelectItem>
                <SelectItem value="3">{t("3")}</SelectItem>
                <SelectItem value="4">{t("4")}</SelectItem>
                <SelectItem value="5">{t("5")}</SelectItem>
                <SelectItem value="6">{t("6")}</SelectItem>
                <SelectItem value="As Needed">{t("As Needed")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Input
            value={medication.reasonForTaking}
            onChange={(e) =>
              onChange({
                ...medication,
                reasonForTaking: e.target.value,
              })
            }
            placeholder={t("Reason for taking medication")}
          />
          {!!onRemove && (
            <div className="w-full flex justify-end">
              <Button variant="outline" onClick={onRemove}>
                {t("Remove Medication")}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
};
